/**
 * ADMIN-T015 — startup cleanup per SPEC §8.6 / §9.4.
 *
 * - Drop drafts older than 30 days.
 * - Drop upload-resume chunk metadata older than 7 days.
 * - Clear expired session tokens.
 * - Clear OAuth interim state UNLESS the current navigation is the SSO callback.
 *
 * Each cleanup branch is testable in isolation through the exported helpers.
 */

const DAY = 86_400_000;
const DRAFT_PREFIX = 'liju.draft.';
const UPLOAD_PREFIX = 'liju.upload.';
const SESSION_KEY = 'liju.session';
const OAUTH_PREFIX = 'liju.oauth.';

export interface CleanupClock {
  now(): number;
}

export interface CleanupDeps {
  storage: Storage;
  clock?: CleanupClock;
  pathname?: string;
}

const defaultClock: CleanupClock = { now: () => Date.now() };

function purgeByPrefix(
  storage: Storage,
  prefix: string,
  cutoff: number,
  pickTimestamp: (raw: string) => number | null,
): string[] {
  const purged: string[] = [];
  for (let i = storage.length - 1; i >= 0; i--) {
    const key = storage.key(i);
    if (!key?.startsWith(prefix)) continue;
    const raw = storage.getItem(key);
    if (!raw) {
      storage.removeItem(key);
      purged.push(key);
      continue;
    }
    const ts = pickTimestamp(raw);
    if (ts === null || ts < cutoff) {
      storage.removeItem(key);
      purged.push(key);
    }
  }
  return purged;
}

function readTimestamp(field: string, raw: string): number | null {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const v = parsed[field];
    return typeof v === 'number' ? v : null;
  } catch {
    return null;
  }
}

export function cleanupDrafts(deps: CleanupDeps): string[] {
  const now = (deps.clock ?? defaultClock).now();
  return purgeByPrefix(deps.storage, DRAFT_PREFIX, now - 30 * DAY, (raw) =>
    readTimestamp('updatedAt', raw),
  );
}

export function cleanupUploadResume(deps: CleanupDeps): string[] {
  const now = (deps.clock ?? defaultClock).now();
  return purgeByPrefix(deps.storage, UPLOAD_PREFIX, now - 7 * DAY, (raw) =>
    readTimestamp('updatedAt', raw),
  );
}

export function cleanupSession(deps: CleanupDeps): boolean {
  const now = (deps.clock ?? defaultClock).now();
  const raw = deps.storage.getItem(SESSION_KEY);
  if (!raw) return false;
  const expiresAt = readTimestamp('expiresAt', raw);
  if (expiresAt === null || expiresAt < now) {
    deps.storage.removeItem(SESSION_KEY);
    return true;
  }
  return false;
}

export function cleanupOauthInterim(deps: CleanupDeps): string[] {
  if (deps.pathname?.startsWith('/sso/callback')) return [];
  const purged: string[] = [];
  for (let i = deps.storage.length - 1; i >= 0; i--) {
    const key = deps.storage.key(i);
    if (key?.startsWith(OAUTH_PREFIX)) {
      deps.storage.removeItem(key);
      purged.push(key);
    }
  }
  return purged;
}

export interface CleanupReport {
  drafts: string[];
  uploads: string[];
  sessionCleared: boolean;
  oauth: string[];
}

export function runStartupCleanup(
  deps: Omit<CleanupDeps, 'pathname'> & { pathname?: string },
): CleanupReport {
  return {
    drafts: cleanupDrafts(deps),
    uploads: cleanupUploadResume(deps),
    sessionCleared: cleanupSession(deps),
    oauth: cleanupOauthInterim(deps),
  };
}
